import type { Action } from 'app/common/utils/actions';
import omit from 'lodash/omit';
import type { Reducer } from 'typesafe-actions/dist/type-helpers';

export type NamespacedState<T> = Record<string, T>;

export default function namespacedReducer<State, RootAction extends Action<any, any, any>>(
  getNamespace: (action: RootAction) => string | undefined,
  initialState: State,
  reducer: (state: State, action: RootAction) => State | null,
  globalReducer?: (state: State, action: RootAction) => State
): Reducer<NamespacedState<State>, RootAction> {
  return (
    // biome-ignore lint/style/useDefaultParameterLast: ignore
    state: NamespacedState<State> = {},
    action: RootAction
  ): NamespacedState<State> => {
    const namespace = getNamespace(action);
    if (namespace) {
      const namespacedState = state[namespace] || initialState;
      const newState = reducer(namespacedState, action);
      if (newState === namespacedState) {
        return state;
      }

      return newState === null ? omit(state, namespace) : { ...state, [namespace]: newState };
    }

    let updated = false;
    if (globalReducer) {
      const update: NamespacedState<State> = {};
      for (const namespace of Object.keys(state)) {
        const namespacedState = state[namespace];
        const newState = globalReducer(namespacedState, action);
        if (newState !== state[namespace]) {
          update[namespace] = newState;
          updated = true;
        }
      }

      return updated ? { ...state, ...update } : state;
    }

    return state;
  };
}
